"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.assessLegacyHeatFilterForRealtimeScoring = void 0;
const metric_catalog_1 = require("./metric-catalog");
const validation_1 = require("./validation");
const legacyOperatorMappings = {
    equalTo: 'eq',
    notEqualTo: 'neq',
    isInArray: 'in',
    isNotInArray: 'notIn',
    lessThan: 'lt',
    lessThanOrEqualTo: 'lte',
    moreThan: 'gt',
    moreThanOrEqualTo: 'gte',
    includes: 'contains',
    isNull: undefined,
    includesOneOf: undefined,
};
const legacyEligibilityMetricMappings = {
    iso3166: 'company.country.iso3166',
    sniClassificationCode: 'company.industry.sni_code',
    sniClassification: 'company.industry.sni_classification',
    operationalStatus: 'company.operational_status',
    registrationDate: 'company.registration_date',
    hasPrivateEquityOwner: 'company.ownership.has_private_equity_owner',
    hasForeignUltimateParent: 'company.ownership.has_foreign_ultimate_parent',
    numberOfOwners: 'company.ownership.owner_count',
    hasShareIssues: 'company.capital.has_share_issues',
    numberOfShares: 'company.capital.share_count',
    zipcode: 'company.location.postal_code',
    muncipality: 'company.location.municipality',
    county: 'company.location.county',
    region: 'company.location.region',
    numberOfEmployees: 'workforce.employees.current',
    h1Content: 'web.page.h1_content',
    h1ContentWords: 'web.page.h1_content_word_counts',
    h1ContentChars: 'web.page.h1_content_character_counts',
    city: 'company.location.city',
};
const revenueMetric = 'financial.revenue.current';
/** Legacy annual-report revenue is stored in thousands of its stated currency. */
const legacyRevenueAmountScale = 1_000;
const legacySignalMetricMappings = {
    companyBuzzIsChangingCeo: 'signal.changed_ceo.within_window',
    companyBuzzIsRaisingCapital: 'signal.raised_capital.within_window',
    companyBuzzIsExpanding: 'signal.expanding.within_window',
};
const legacySignalRuleIdPrefixes = {
    companyBuzzIsChangingCeo: 'changed-ceo',
    companyBuzzIsRaisingCapital: 'raised-capital',
    companyBuzzIsExpanding: 'expanding',
};
const filterOperators = Object.keys(legacyOperatorMappings);
const toRealtimePredicateValue = (value) => {
    if (value instanceof Date && !Number.isNaN(value.valueOf())) {
        return value.toISOString();
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        return value;
    }
    if (Array.isArray(value) && value.every((item) => typeof item === 'number')) {
        return value;
    }
    return undefined;
};
const toIsoDatePredicateValue = (value) => {
    const toIsoDate = (candidate) => {
        const date = candidate.slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date))
            return undefined;
        const parsed = new Date(`${date}T00:00:00.000Z`);
        return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === date ? date : undefined;
    };
    if (typeof value === 'string')
        return toIsoDate(value);
    if (!Array.isArray(value) || !value.every((item) => typeof item === 'string'))
        return undefined;
    const dates = value.map(toIsoDate);
    return dates.every((date) => date !== undefined) ? dates : undefined;
};
const predicateFromLegacyFilter = (legacyPath, legacyFilter, metric, semanticMessage) => {
    if (legacyFilter.ignoreIfNull === true) {
        return {
            finding: {
                legacyPath,
                disposition: 'unsupported',
                message: 'ignoreIfNull has no equivalent V1 semantics and requires manual translation.',
                suggestedMetric: metric,
            },
        };
    }
    const presentOperators = filterOperators.filter((operator) => legacyFilter[operator] !== undefined);
    if (presentOperators.length === 0) {
        return {
            finding: {
                legacyPath,
                disposition: 'unsupported',
                message: 'The legacy condition has no comparison operator that can be translated to V1.',
                suggestedMetric: metric,
            },
        };
    }
    const metricDefinition = (0, metric_catalog_1.findRealtimeMetric)(metric);
    const conditions = presentOperators.flatMap((legacyOperator) => {
        if (legacyOperator === 'includesOneOf') {
            const values = legacyFilter.includesOneOf;
            const supportsContains = metricDefinition?.supportedOperators.includes('contains') === true;
            const valuesMatchArrayType = (Array.isArray(values) && values.every((value) => typeof value === 'string')) ||
                (metricDefinition?.valueType === 'number_array' &&
                    Array.isArray(values) &&
                    values.every((value) => typeof value === 'number' && Number.isFinite(value)));
            if (Array.isArray(values) && values.length > 0 && valuesMatchArrayType && supportsContains) {
                return [
                    {
                        kind: 'any',
                        conditions: values.map((value) => ({
                            kind: 'predicate',
                            metric,
                            operator: 'contains',
                            value,
                        })),
                    },
                ];
            }
            return [];
        }
        const rawValue = toRealtimePredicateValue(legacyFilter[legacyOperator]);
        const value = rawValue === undefined
            ? undefined
            : metricDefinition?.valueType === 'date'
                ? toIsoDatePredicateValue(rawValue)
                : rawValue;
        const mappedOperator = legacyOperatorMappings[legacyOperator];
        const operator = Array.isArray(value) && legacyOperator === 'equalTo'
            ? 'in'
            : Array.isArray(value) && legacyOperator === 'notEqualTo'
                ? 'notIn'
                : mappedOperator;
        const valueMatchesMetric = (metricDefinition?.valueType === 'number' && typeof value === 'number') ||
            (metricDefinition?.valueType === 'boolean' && typeof value === 'boolean') ||
            (metricDefinition?.valueType === 'string' &&
                (typeof value === 'string' || (Array.isArray(value) && value.every((item) => typeof item === 'string')))) ||
            (metricDefinition?.valueType === 'string_array' && typeof value === 'string') ||
            (metricDefinition?.valueType === 'date' &&
                (typeof value === 'string' || (Array.isArray(value) && value.every((item) => typeof item === 'string'))));
        return operator === undefined ||
            value === undefined ||
            metricDefinition === undefined ||
            !metricDefinition.supportedOperators.includes(operator) ||
            !valueMatchesMetric ||
            (Array.isArray(value) && value.length === 0)
            ? []
            : [{ kind: 'predicate', metric, operator, value }];
    });
    if (conditions.length !== presentOperators.length) {
        return {
            finding: {
                legacyPath,
                disposition: 'unsupported',
                message: `At least one legacy operator or value has no safe V1 translation for ${metric}.`,
                suggestedMetric: metric,
            },
        };
    }
    return {
        finding: {
            legacyPath,
            disposition: semanticMessage === undefined ? 'direct' : 'semantic_change',
            message: semanticMessage ?? 'Can be expressed with the same comparison operator in V1.',
            suggestedMetric: metric,
        },
        condition: conditions.length === 1 ? conditions[0] : { kind: 'all', conditions },
    };
};
const revenuePredicateFromLegacyFilters = (currency, netSales, options) => {
    const currencyPath = 'filters.currency';
    const revenuePath = 'filters.netSales';
    const selectedCurrency = currency === undefined
        ? options.revenueCurrency
        : currency.ignoreIfNull !== true &&
            typeof currency.equalTo === 'string' &&
            filterOperators.every((operator) => operator === 'equalTo' || currency[operator] === undefined) &&
            /^[A-Z]{3}$/.test(currency.equalTo)
            ? currency.equalTo
            : undefined;
    if (selectedCurrency === undefined || !/^[A-Z]{3}$/.test(selectedCurrency)) {
        return {
            findings: [
                {
                    legacyPath: currencyPath,
                    disposition: 'unsupported',
                    message: 'Revenue can only be translated automatically with an explicit equalTo currency condition using an uppercase ISO currency code.',
                    suggestedMetric: revenueMetric,
                },
                {
                    legacyPath: revenuePath,
                    disposition: 'unsupported',
                    message: 'Revenue requires a compatible currency condition for a safe V1 translation.',
                    suggestedMetric: revenueMetric,
                },
            ],
        };
    }
    if (netSales.ignoreIfNull === true) {
        return {
            findings: [
                {
                    legacyPath: revenuePath,
                    disposition: 'unsupported',
                    message: 'ignoreIfNull has no equivalent V1 semantics for revenue and requires manual translation.',
                    suggestedMetric: revenueMetric,
                },
            ],
        };
    }
    const presentOperators = filterOperators.filter((operator) => netSales[operator] !== undefined);
    if (presentOperators.length === 0) {
        return {
            findings: [
                {
                    legacyPath: revenuePath,
                    disposition: 'unsupported',
                    message: 'Revenue has no numeric comparison operator supported by financial.revenue.current.',
                    suggestedMetric: revenueMetric,
                },
            ],
        };
    }
    const conditions = presentOperators.flatMap((legacyOperator) => {
        const amount = netSales[legacyOperator];
        const operator = legacyOperatorMappings[legacyOperator];
        const normalizedAmount = typeof amount === 'number' ? amount * legacyRevenueAmountScale : undefined;
        return operator === undefined ||
            typeof amount !== 'number' ||
            !Number.isFinite(amount) ||
            normalizedAmount === undefined ||
            !Number.isFinite(normalizedAmount) ||
            !(0, metric_catalog_1.findRealtimeMetric)(revenueMetric)?.supportedOperators.includes(operator)
            ? []
            : [
                {
                    kind: 'predicate',
                    metric: revenueMetric,
                    operator,
                    value: { amount: normalizedAmount, currency: selectedCurrency },
                },
            ];
    });
    if (conditions.length !== presentOperators.length) {
        return {
            findings: [
                {
                    legacyPath: revenuePath,
                    disposition: 'unsupported',
                    message: 'At least one revenue operator has no safe V1 translation for financial.revenue.current.',
                    suggestedMetric: revenueMetric,
                },
            ],
        };
    }
    return {
        findings: [
            {
                legacyPath: currencyPath,
                disposition: 'semantic_change',
                message: 'V1 compares revenue in the explicitly selected currency and reads raw financial sources rather than the master materialization; no FX conversion is applied and results must be compared in shadow mode.',
                suggestedMetric: revenueMetric,
            },
            {
                legacyPath: revenuePath,
                disposition: 'semantic_change',
                message: 'V1 normalizes legacy revenue from thousands to absolute currency units and only compares the same currency; results must be compared in shadow mode before staging.',
                suggestedMetric: revenueMetric,
            },
        ],
        condition: conditions.length === 1 ? conditions[0] : { kind: 'all', conditions },
    };
};
const scoreRuleFromLegacyWeight = (legacyPath, condition, metric, ruleId, score) => {
    if (condition === undefined || filterOperators.every((operator) => condition[operator] === undefined)) {
        return {
            finding: {
                legacyPath,
                disposition: 'unsupported',
                message: 'Unconditional legacy scores must not be translated implicitly to V1.',
                suggestedMetric: metric,
            },
        };
    }
    if (!Number.isFinite(score)) {
        return {
            finding: {
                legacyPath,
                disposition: 'unsupported',
                message: 'The legacy score must be finite to be translated to V1.',
                suggestedMetric: metric,
            },
        };
    }
    if (condition.equalTo !== true ||
        condition.ignoreIfNull === true ||
        filterOperators.some((operator) => operator !== 'equalTo' && condition[operator] !== undefined)) {
        return {
            finding: {
                legacyPath,
                disposition: 'unsupported',
                message: 'Only the positive Buzz condition equalTo: true can become a V1 draft; negative or compound conditions require explicit null semantics.',
                suggestedMetric: metric,
            },
        };
    }
    const translated = predicateFromLegacyFilter(legacyPath, condition, metric);
    if (translated.condition === undefined) {
        return { finding: translated.finding };
    }
    return {
        finding: {
            legacyPath,
            disposition: 'semantic_change',
            message: 'V1 uses raw Buzz data with a 90-day window and a direct or mapped organization identity instead of the legacy fact’s two-week window; compare total scores in shadow mode and approve the canary before activation.',
            suggestedMetric: metric,
        },
        rule: { id: ruleId, when: translated.condition, score },
    };
};
/**
 * Classifies legacy structured filters and weights without storing or activating anything.
 *
 * The generated draft intentionally remains conservative: cross-source fields
 * and buzz windows are labelled `semantic_change`, while unsupported legacy
 * behavior prevents a draft from being returned.
 */
const assessLegacyHeatFilterForRealtimeScoring = (configuration, options = {}) => {
    const findings = [];
    const eligibilityConditions = [];
    const scoreRules = [];
    for (const [field, filter] of Object.entries(configuration.filters ?? {})) {
        if (field === 'currency') {
            if (configuration.filters?.netSales !== undefined)
                continue;
            findings.push({
                legacyPath: 'filters.currency',
                disposition: 'unsupported',
                message: 'Currency without a revenue condition has no safe V1 translation.',
            });
            continue;
        }
        if (field === 'netSales') {
            const translated = revenuePredicateFromLegacyFilters(configuration.filters?.currency, filter, options);
            findings.push(...translated.findings);
            if (translated.condition !== undefined)
                eligibilityConditions.push(translated.condition);
            continue;
        }
        const metric = legacyEligibilityMetricMappings[field];
        if (metric === undefined) {
            findings.push({
                legacyPath: `filters.${field}`,
                disposition: 'unsupported',
                message: 'The field is missing from the V1 metric catalog and requires manual translation.',
            });
            continue;
        }
        const semanticMessage = field === 'iso3166'
            ? 'V1 derives country from the organization-number prefix in raw company_master data rather than iso_3166 in the master materialization; compare results in shadow mode before staging.'
            : field === 'numberOfEmployees'
                ? 'V1 reads current workforce data rather than the master materialization; compare results in shadow mode before staging.'
                : field === 'city'
                    ? 'V1 reads city directly from raw company_master data rather than the master materialization; compare results in shadow mode before staging.'
                    : field === 'sniClassification'
                        ? 'V1 reads SNI classification directly from raw company_master data rather than the master materialization; compare results in shadow mode before staging.'
                        : undefined;
        const translated = predicateFromLegacyFilter(`filters.${field}`, filter, metric, semanticMessage);
        findings.push(translated.finding);
        if (translated.condition !== undefined)
            eligibilityConditions.push(translated.condition);
    }
    for (const [field, weights] of Object.entries(configuration.weights ?? {})) {
        const metric = legacySignalMetricMappings[field];
        if (metric === undefined) {
            findings.push({
                legacyPath: `weights.${field}`,
                disposition: 'unsupported',
                message: 'The legacy score field is missing from the V1 metric catalog and requires manual translation.',
            });
            continue;
        }
        weights.forEach((weight, index) => {
            const legacyPath = `weights.${field}[${String(index)}]`;
            if (weight.strategy !== 'static') {
                findings.push({
                    legacyPath,
                    disposition: 'unsupported',
                    message: 'A proportional legacy score has no implicit V1 translation.',
                    suggestedMetric: metric,
                });
                return;
            }
            const ruleIdPrefix = legacySignalRuleIdPrefixes[field];
            if (ruleIdPrefix === undefined) {
                findings.push({
                    legacyPath,
                    disposition: 'unsupported',
                    message: 'The legacy score field has no stable V1 rule id and requires manual translation.',
                    suggestedMetric: metric,
                });
                return;
            }
            const translated = scoreRuleFromLegacyWeight(`${legacyPath}.condition`, weight.condition, metric, `${ruleIdPrefix}-${String(index + 1)}`, weight.weight);
            findings.push(translated.finding);
            if (translated.rule !== undefined)
                scoreRules.push(translated.rule);
        });
    }
    if (eligibilityConditions.length === 0 && !findings.some((finding) => finding.disposition === 'unsupported')) {
        findings.push({
            legacyPath: 'filters',
            disposition: 'unsupported',
            message: 'V1 requires at least one eligibility condition; an empty legacy selection must not become an accidental match of all companies.',
        });
    }
    const requiresManualTranslation = findings.some((finding) => finding.disposition === 'unsupported');
    if (requiresManualTranslation) {
        return { findings, requiresManualTranslation };
    }
    const eligibility = eligibilityConditions.length === 1 ? eligibilityConditions[0] : { kind: 'all', conditions: eligibilityConditions };
    const suggestedDefinition = { version: 'v1', eligibility, scoreRules };
    const validationErrors = (0, validation_1.validateRealtimeScoringDefinition)(suggestedDefinition);
    if (validationErrors.length > 0) {
        return {
            findings: [
                ...findings,
                {
                    legacyPath: 'definition',
                    disposition: 'unsupported',
                    message: `The proposed V1 draft violates the catalog rule: ${validationErrors[0]?.message}.`,
                },
            ],
            requiresManualTranslation: true,
        };
    }
    return { findings, suggestedDefinition, requiresManualTranslation };
};
exports.assessLegacyHeatFilterForRealtimeScoring = assessLegacyHeatFilterForRealtimeScoring;
//# sourceMappingURL=migration-assessment.js.map